import React from 'react';
import dayjs from 'dayjs';
import { PrintPageHeader, PrintFooter, printStyles } from './PrintBase';

const formatCurrency = (val) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val || 0);

const DashboardPrint = React.forwardRef(function DashboardPrint({ data }, ref) {
  const productosBajoStock = data?.productos_bajo_stock ?? [];
  const productosSobreStock = data?.productos_sobre_stock ?? [];
  const movimientosRecientes = data?.movimientos_recientes ?? [];
  const topProductos = data?.top_productos_movimiento ?? [];
  
  return (
    <div ref={ref} style={printStyles.page}>
      <PrintPageHeader
        titulo="Dashboard — Resumen del Inventario"
        subtitulo={`Datos del mes de ${dayjs().format('MMMM YYYY')}`}
      />

      {/* KPI Grid */}
      <div style={printStyles.kpiGrid}>
        <div style={printStyles.kpiCard}>
          <div style={printStyles.kpiLabel}>Total Productos Activos</div>
          <div style={{ ...printStyles.kpiValue, color: '#1677ff' }}>
            {parseFloat(data?.total_productos || 0).toLocaleString('es-MX')}
          </div>
        </div>
        <div style={printStyles.kpiCard}>
          <div style={printStyles.kpiLabel}>Valor del Inventario</div>
          <div style={{ ...printStyles.kpiValue, color: '#722ed1', fontSize: '12pt' }}>
            {formatCurrency(data?.valor_inventario)}
          </div>
        </div>
        <div style={printStyles.kpiCard}>
          <div style={printStyles.kpiLabel}>Entradas del Mes (uds.)</div>
          <div style={{ ...printStyles.kpiValue, color: '#52c41a' }}>
            {parseFloat(data?.total_entradas || 0).toLocaleString('es-MX')}
          </div>
        </div>
        <div style={printStyles.kpiCard}>
          <div style={printStyles.kpiLabel}>Salidas del Mes (uds.)</div>
          <div style={{ ...printStyles.kpiValue, color: '#ff4d4f' }}>
            {parseFloat(data?.total_salidas || 0).toLocaleString('es-MX')}
          </div>
        </div>
      </div>

      {/* Productos con Stock Bajo */}
      <div style={printStyles.sectionTitle}>
        Productos con Stock Bajo o Sin Stock
        {(data?.total_bajo_stock ?? 0) > 0 && ` (${data.total_bajo_stock} productos)`}
      </div>
      {productosBajoStock.length > 0 ? (
        <table style={printStyles.table}>
          <thead>
            <tr>
              <th style={printStyles.th}>Clave</th>
              <th style={printStyles.th}>Nombre</th>
              <th style={printStyles.th}>Categoría</th>
              <th style={printStyles.th}>Unidad</th>
              <th style={{ ...printStyles.th, textAlign: 'right' }}>Stock Actual</th>
              <th style={{ ...printStyles.th, textAlign: 'right' }}>Stock Mínimo</th>
              <th style={{ ...printStyles.th, textAlign: 'center' }}>Estado</th>
            </tr>
          </thead>
          <tbody>
            {productosBajoStock.map((prod) => {
              const sinStock = parseFloat(prod.stock_actual) === 0;
              const rowStyle = sinStock ? { backgroundColor: '#fff1f0' } : { backgroundColor: '#fffbe6' };
              return (
                <tr key={prod.id} style={rowStyle}>
                  <td style={printStyles.td}>{prod.clave || '—'}</td>
                  <td style={printStyles.td}>{prod.nombre || '—'}</td>
                  <td style={printStyles.td}>{prod.categoria_nombre || '—'}</td>
                  <td style={printStyles.tdCenter}>{prod.unidad_medida || '—'}</td>
                  <td style={{ ...printStyles.tdRight, color: sinStock ? '#cf1322' : '#d46b08', fontWeight: 'bold' }}>
                    {parseFloat(prod.stock_actual || 0).toLocaleString('es-MX')} {prod.unidad_medida}
                  </td>
                  <td style={printStyles.tdRight}>
                    {parseFloat(prod.stock_minimo || 0).toLocaleString('es-MX')} {prod.unidad_medida}
                  </td>
                  <td style={{ ...printStyles.tdCenter, color: sinStock ? '#cf1322' : '#d46b08', fontWeight: 'bold' }}>
                    {sinStock ? 'Sin Stock' : 'Stock Bajo'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p style={{ color: '#389e0d', fontStyle: 'italic', fontSize: '9pt' }}>
          Todos los productos tienen stock suficiente.
        </p>
      )}

      {/* Productos sobre Stock Máximo */}
      {productosSobreStock.length > 0 && (
        <>
          <div style={printStyles.sectionTitle}>
            Productos que Exceden el Stock Máximo ({productosSobreStock.length})
          </div>
          <table style={printStyles.table}>
            <thead>
              <tr>
                <th style={printStyles.th}>Clave</th>
                <th style={printStyles.th}>Nombre</th>
                <th style={printStyles.th}>Categoría</th>
                <th style={{ ...printStyles.th, textAlign: 'right' }}>Stock Actual</th>
                <th style={{ ...printStyles.th, textAlign: 'right' }}>Stock Máximo</th>
                <th style={{ ...printStyles.th, textAlign: 'right' }}>Exceso</th>
              </tr>
            </thead>
            <tbody>
              {productosSobreStock.map((prod) => {
                const exceso = (parseFloat(prod.stock_actual) || 0) - (parseFloat(prod.stock_maximo) || 0);
                return (
                  <tr key={prod.id} style={{ backgroundColor: '#f9f0ff' }}>
                    <td style={printStyles.td}>{prod.clave || '—'}</td>
                    <td style={printStyles.td}>{prod.nombre || '—'}</td>
                    <td style={printStyles.td}>{prod.categoria_nombre || '—'}</td>
                    <td style={{ ...printStyles.tdRight, color: '#531dab', fontWeight: 'bold' }}>
                      {parseFloat(prod.stock_actual || 0).toLocaleString('es-MX')} {prod.unidad_medida}
                    </td>
                    <td style={printStyles.tdRight}>
                      {parseFloat(prod.stock_maximo || 0).toLocaleString('es-MX')} {prod.unidad_medida}
                    </td>
                    <td style={{ ...printStyles.tdRight, color: '#531dab', fontWeight: 'bold' }}>
                      +{exceso.toLocaleString('es-MX')} {prod.unidad_medida}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {/* Movimientos Recientes */}
      <div style={printStyles.sectionTitle}>
        Movimientos Recientes (últimos {movimientosRecientes.length})
      </div>
      {movimientosRecientes.length > 0 ? (
        <table style={printStyles.table}>
          <thead>
            <tr>
              <th style={printStyles.th}>Fecha</th>
              <th style={printStyles.th}>Clave</th>
              <th style={printStyles.th}>Producto</th>
              <th style={{ ...printStyles.th, textAlign: 'center' }}>Tipo</th>
              <th style={{ ...printStyles.th, textAlign: 'right' }}>Cantidad</th>
              <th style={printStyles.th}>Motivo</th>
              <th style={printStyles.th}>Referencia</th>
            </tr>
          </thead>
          <tbody>
            {movimientosRecientes.map((mov) => {
              const esEntrada = mov.tipo === 'entrada';
              return (
                <tr key={mov.id} style={esEntrada ? printStyles.trEntrada : printStyles.trSalida}>
                  <td style={printStyles.td}>{dayjs(mov.created_at).format('DD/MM/YY HH:mm')}</td>
                  <td style={printStyles.td}>{mov.clave || '—'}</td>
                  <td style={printStyles.td}>{mov.producto_nombre || '—'}</td>
                  <td style={{ ...printStyles.tdCenter, color: esEntrada ? '#389e0d' : '#cf1322', fontWeight: 'bold' }}>
                    {esEntrada ? 'Entrada' : 'Salida'}
                  </td>
                  <td style={printStyles.tdRight}>
                    {parseFloat(mov.cantidad || 0).toLocaleString('es-MX')} {mov.unidad_medida}
                  </td>
                  <td style={printStyles.td}>{mov.motivo || '—'}</td>
                  <td style={printStyles.td}>{mov.referencia || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p style={{ color: '#888', fontStyle: 'italic', fontSize: '9pt' }}>
          Sin movimientos registrados.
        </p>
      )}

      {/* Top 5 Productos */}
      {topProductos.length > 0 && (
        <>
          <div style={printStyles.sectionTitle}>Top 5 Productos con Más Movimientos</div>
          <table style={printStyles.table}>
            <thead>
              <tr>
                <th style={{ ...printStyles.th, textAlign: 'center', width: '30pt' }}>#</th>
                <th style={printStyles.th}>Clave</th>
                <th style={printStyles.th}>Nombre</th>
                <th style={printStyles.th}>Categoría</th>
                <th style={{ ...printStyles.th, textAlign: 'center' }}>Movimientos</th>
                <th style={{ ...printStyles.th, textAlign: 'right' }}>Entradas</th>
                <th style={{ ...printStyles.th, textAlign: 'right' }}>Salidas</th>
              </tr>
            </thead>
            <tbody>
              {topProductos.map((prod, index) => (
                <tr key={prod.id} style={index % 2 === 0 ? {} : { backgroundColor: '#fafafa' }}>
                  <td style={{ ...printStyles.tdCenter, fontWeight: 'bold', color: '#1677ff' }}>
                    {index + 1}
                  </td>
                  <td style={printStyles.td}>{prod.clave || '—'}</td>
                  <td style={printStyles.td}>{prod.nombre || '—'}</td>
                  <td style={printStyles.td}>{prod.categoria_nombre || '—'}</td>
                  <td style={{ ...printStyles.tdCenter, fontWeight: 'bold', color: '#1677ff' }}>
                    {prod.total_movimientos || 0}
                  </td>
                  <td style={{ ...printStyles.tdRight, color: '#389e0d', fontWeight: 600 }}>
                    {parseFloat(prod.total_entradas || 0).toLocaleString('es-MX')}
                  </td>
                  <td style={{ ...printStyles.tdRight, color: '#cf1322', fontWeight: 600 }}>
                    {parseFloat(prod.total_salidas || 0).toLocaleString('es-MX')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <PrintFooter />
    </div>
  );
});

export default DashboardPrint;
